// Admin settings page: save feedback.
//
// The page's three save surfaces (Base URL form, scan settings form,
// per-row tunable forms) are htmx-driven; before this module none of
// them gave visible confirmation — the Base URL form re-rendered into
// an identical-looking form, and the scan form swapped raw JSON text
// over its card (fixed server-side by hx-swap="none" + this toast).
// Toasts are the sanctioned client-local island, so the notification
// lives here; no inline scripts (CSP hash discipline).
import { showToast } from "./toast";

// Map a settings save form to its success-toast label. Returns null for
// elements that are not settings save forms, so the delegated listener
// can filter in one call. Keyed by the data-settings-save attribute
// stamped on each form in templates/admin_settings.templ.
export function saveLabelFor(elt: EventTarget | null | undefined): string | null {
  const form = elt instanceof Element ? elt : null;
  const kind = form?.getAttribute("data-settings-save");
  switch (kind) {
    case "settings":
      return "Settings saved";
    case "scan":
      return "Scan settings saved";
    case "tunable":
      return "Setting saved";
    default:
      return null;
  }
}

// Pull a human-readable error out of a failed save response. The config
// endpoints return JSON `{"error": "..."}`; the tunable endpoint returns
// an HTML span on 400 — markup must never reach a toast, so non-JSON
// bodies degrade to the status line.
export function extractSaveError(xhr: XMLHttpRequest | undefined): string {
  const text = (xhr?.responseText || "").trim();
  if (text) {
    try {
      const parsed = JSON.parse(text) as { error?: string; message?: string };
      if (parsed.error) return parsed.error;
      if (parsed.message) return parsed.message;
    } catch {
      // HTML or plain-text body — fall through.
    }
  }
  return `Save failed (HTTP ${xhr?.status || 0})`;
}

// The element htmx issued the request FROM. htmx 2.x dispatches request
// lifecycle events with detail.elt set to the element the event was
// dispatched on — for FORM submissions that is an ancestor wrapper, and
// the real requesting element lives on detail.requestConfig.elt
// (verified empirically: afterRequest on the settings forms reports
// elt=DIV wrapper, requestConfig.elt=FORM). Element-triggered requests
// (htmx.trigger on the search input) set both to the same element, so
// prefer requestConfig.elt and fall back to elt.
function requestingElement(
  detail:
    | {
        requestConfig?: { elt?: EventTarget };
        elt?: EventTarget;
      }
    | null
    | undefined,
): Element | null {
  const rc = detail?.requestConfig?.elt;
  if (rc instanceof Element) return rc;
  const elt = detail?.elt;
  return elt instanceof Element ? elt : null;
}

function wireSettingsSaveFeedback(): void {
  // htmx fires lifecycle events on the REQUESTING element; body-level
  // delegation covers all three forms (and rows swapped in later).
  document.body.addEventListener("htmx:afterRequest", (evt) => {
    const e = evt as CustomEvent<{
      elt: EventTarget;
      requestConfig?: { elt?: EventTarget };
      successful: boolean;
      xhr?: XMLHttpRequest;
    }>;
    const label = saveLabelFor(requestingElement(e.detail));
    if (!label) return;
    if (e.detail.successful) {
      showToast(label, "success");
    } else {
      showToast(`Save failed: ${extractSaveError(e.detail.xhr)}`, "error");
    }
  });

  // A request that never gets a response (network down, server restart)
  // fires sendError, not afterRequest — without this the button just
  // re-enables silently and the user can't tell the save never landed.
  document.body.addEventListener("htmx:sendError", (evt) => {
    const e = evt as CustomEvent<{ elt: EventTarget; requestConfig?: { elt?: EventTarget } }>;
    if (!saveLabelFor(requestingElement(e.detail))) return;
    showToast("Save failed: network error", "error");
  });
}

// The bundle is a deferred module script: the DOM is parsed when this
// runs, so direct wiring matches the other modules (admin.ts etc.).
wireSettingsSaveFeedback();
